import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { fetchBlog, fetchDiseases } from "@/lib/api";
import { displayDiseaseNameBySlug } from "@/lib/diseaseDisplay";
import BlogBody from "@/components/BlogBody";
import { DISEASE } from "@/constants/testIds";

export default function BlogDetail() {
  const { id } = useParams();
  const [blog, setBlog] = useState(null);
  const [diseases, setDiseases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(false);
    Promise.all([fetchBlog(id), fetchDiseases().catch(() => [])])
      .then(([data, dx]) => {
        if (!alive) return;
        setBlog(data);
        setDiseases(dx || []);
      })
      .catch(() => {
        if (!alive) return;
        setError(true);
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [id]);

  const diseaseName = (slug) => displayDiseaseNameBySlug(slug, diseases) || "Disease";

  if (loading) {
    return (
      <main className="max-w-3xl mx-auto px-6 md:px-10 py-20 flex justify-center text-slate-500">
        <Loader2 className="animate-spin mr-2" /> Loading…
      </main>
    );
  }

  if (error || !blog) {
    return (
      <main className="max-w-3xl mx-auto px-6 md:px-10 py-20">
        <Link
          to="/know-your-disease"
          data-testid={DISEASE.blogBack}
          className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
        >
          <ChevronLeft size={16} /> Back to articles
        </Link>
        <h1 className="font-serif-display text-4xl text-slate-900 mt-6">Article not found</h1>
        <p className="text-slate-600 mt-3">
          The article you're looking for may have been moved or removed.
        </p>
      </main>
    );
  }

  return (
    <main data-testid={DISEASE.blogDetail} className="max-w-3xl mx-auto px-6 md:px-10 py-12 md:py-20">
      <Link
        to="/know-your-disease"
        data-testid={DISEASE.blogBack}
        className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
      >
        <ChevronLeft size={16} /> All articles
      </Link>

      <Badge
        variant="secondary"
        className="bg-emerald-50 text-slate-700 border-emerald-100 mt-7"
      >
        {diseaseName(blog.disease)}
      </Badge>

      <h1 className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-slate-900 mt-4 leading-tight">
        {blog.title}
      </h1>

      {blog.excerpt ? (
        <p className="text-slate-600 mt-5 text-lg leading-relaxed">{blog.excerpt}</p>
      ) : null}

      {blog.cover_image ? (
        <div className="mt-8 rounded-2xl overflow-hidden border border-slate-200 bg-slate-100">
          <img
            src={blog.cover_image}
            alt={blog.title}
            className="w-full h-auto object-cover"
          />
        </div>
      ) : null}

      <article className="mt-10">
        <BlogBody html={blog.body} />
      </article>

      <div className="mt-14 rounded-2xl bg-slate-900 text-white p-8 md:p-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <div className="font-serif-display text-2xl md:text-3xl">Want a personal opinion?</div>
          <div className="text-slate-300 text-sm mt-2">
            Get a full 20-minute consultation at Namya Health.
          </div>
        </div>
        <Link to="/#book">
          <Button className="rounded-full h-11 px-7 bg-[hsl(var(--primary-bold))] text-slate-900 font-semibold hover:bg-[hsl(var(--primary-bold))]/90">
            Book Appointment
          </Button>
        </Link>
      </div>

      <p className="text-xs text-slate-500 mt-8">
        This article is educational and does not replace personalised medical advice.
      </p>
    </main>
  );
}
